import { useEffect, useRef } from 'react';
import { cn } from '@rocco/utils/cn';
import { usePrefersReducedMotion } from '~/ui-rocco/pages/detail-shared/use-prefers-reduced-motion';

export interface HoverLoopVideoSource {
    webm: string;
    mp4: string;
    /** The loop's own first frame: the still it shows until played. */
    poster: string;
}

/**
 * A short loop that sits on its first frame until the card around it is
 * pointed at (or focused), plays while it is, and goes back to that frame
 * when the pointer leaves. The card is the nearest `group` ancestor, the same
 * element whose hover brings the card to colour.
 *
 * Desktop widths only: the sources carry a media query, so a phone fetches
 * no video and keeps the card's photo. Under reduced motion it stays on the
 * still.
 */
export const HoverLoopVideo = ({
    video,
    className,
}: {
    video: HoverLoopVideoSource;
    className?: string;
}) => {
    const ref = useRef<HTMLVideoElement>(null);
    const reducedMotion = usePrefersReducedMotion();

    useEffect(() => {
        const el = ref.current;
        const card = el?.closest<HTMLElement>('.group');
        if (!el || !card) return;
        const rewind = () => {
            el.pause();
            el.currentTime = 0;
        };
        // Turning reduced motion on mid-play puts the loop back on its still.
        if (reducedMotion) {
            rewind();
            return;
        }
        const play = () => {
            el.play().catch(() => {
                // Autoplay refused: the still stays up.
            });
        };
        // Focus moving between links in the same card keeps it playing.
        const blur = (event: FocusEvent) => {
            if (card.contains(event.relatedTarget as Node | null)) return;
            rewind();
        };
        card.addEventListener('mouseenter', play);
        card.addEventListener('mouseleave', rewind);
        card.addEventListener('focusin', play);
        card.addEventListener('focusout', blur);
        return () => {
            card.removeEventListener('mouseenter', play);
            card.removeEventListener('mouseleave', rewind);
            card.removeEventListener('focusin', play);
            card.removeEventListener('focusout', blur);
        };
    }, [reducedMotion]);

    return (
        <video
            ref={ref}
            aria-hidden="true"
            poster={video.poster}
            muted
            loop
            playsInline
            preload="metadata"
            className={cn('absolute inset-0 size-full object-cover', className)}
        >
            <source
                src={video.webm}
                type="video/webm"
                media="(min-width: 48rem)"
            />
            <source
                src={video.mp4}
                type="video/mp4"
                media="(min-width: 48rem)"
            />
        </video>
    );
};
